/**
 * Frontier runs — listing with filters and manual emergency stop
 * (REQ-FRT-016/REQ-FRT-013).
 *
 * The stop actions require the specific permission `frontier#emergency_stop`
 * (`can_stop` in the emergency state) and act on the selected executions
 * (idempotent bulk stop) or on all running executions (tenant switch).
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PlayCircle } from 'lucide-react';
import { PageHeader } from '@vanguard-atlas/shared';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Card,
  CardContent,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';
import { useI18n } from '@/i18n';
import { StatusChip, formatDateTime } from '../components/FrontierGuard';
import {
  useBulkStopRuns,
  useEmergencyState,
  useFrontierRoutines,
  useFrontierRuns,
  useSetEmergencySwitch,
} from '../hooks/useFrontier';
import type { BulkStopResult, RunStatus } from '../types';

const STATUS_FILTERS: Array<RunStatus | ''> = [
  '',
  'queued',
  'running',
  'waiting_input',
  'waiting_approval',
  'blocked',
  'paused',
  'completed',
  'failed',
  'expired',
  'cancelled',
];

/** Estados desde los que el API permite detener una ejecución (REQ-FRT-013). */
const STOPPABLE_STATUSES: ReadonlySet<string> = new Set([
  'queued',
  'running',
  'retrying',
  'blocked',
  'paused',
  'waiting_input',
  'waiting_approval',
]);

export default function FrontierRunsPage() {
  const { t } = useI18n();
  const [status, setStatus] = useState<RunStatus | ''>('');
  const [routineId, setRoutineId] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirm, setConfirm] = useState<'selected' | 'all' | 'block' | 'clear' | null>(null);
  const [lastResult, setLastResult] = useState<BulkStopResult | null>(null);
  const [actionError, setActionError] = useState(false);

  const routines = useFrontierRoutines();
  const { data, isLoading, isError } = useFrontierRuns({
    status: status || undefined,
    routine_id: routineId || undefined,
  });
  const emergency = useEmergencyState();
  const bulkStop = useBulkStopRuns();
  const setSwitch = useSetEmergencySwitch();

  const canStop = emergency.data?.can_stop === true;
  const switchActive = emergency.data?.tenant_switch === true;
  const items = data?.items ?? [];
  const stoppableIds = useMemo(
    () => items.filter((run) => STOPPABLE_STATUSES.has(run.status)).map((run) => run.id),
    [items],
  );
  const allSelected = stoppableIds.length > 0 && stoppableIds.every((id) => selected.has(id));
  const pending = bulkStop.isPending || setSwitch.isPending;

  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        stoppableIds.forEach((id) => next.delete(id));
      } else {
        stoppableIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const toggleOne = (runId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(runId)) next.delete(runId);
      else next.add(runId);
      return next;
    });
  };

  const stopSelected = async () => {
    try {
      const result = await bulkStop.mutateAsync(Array.from(selected));
      setLastResult(result);
      setSelected(new Set());
      setActionError(false);
    } catch {
      setActionError(true);
    } finally {
      setConfirm(null);
    }
  };

  const stopAll = async () => {
    try {
      await setSwitch.mutateAsync({ scope: 'tenant', enabled: true });
      setLastResult(null);
      setSelected(new Set());
      setActionError(false);
    } catch {
      setActionError(true);
    } finally {
      setConfirm(null);
    }
  };

  const reactivate = async () => {
    try {
      await setSwitch.mutateAsync({ scope: 'tenant', enabled: false });
      setLastResult(null);
      setActionError(false);
    } catch {
      setActionError(true);
    } finally {
      setConfirm(null);
    }
  };

  return (
    <div className="p-6 space-y-4" data-testid="frontier-runs">
      <PageHeader icon={PlayCircle} title={t('frontier.runs.title')} testId="frontier-runs-header" />

      <div className="flex flex-wrap items-center gap-3 text-sm">
        <label className="flex items-center gap-2">
          <span className="text-gray-500 dark:text-gray-400">
            {t('frontier.runs.filterStatus')}
          </span>
          <select
            aria-label={t('frontier.runs.filterStatus')}
            className="rounded border border-input bg-background px-2 py-1"
            value={status}
            onChange={(event) => setStatus(event.target.value as RunStatus | '')}
          >
            {STATUS_FILTERS.map((option) => (
              <option key={option || 'all'} value={option}>
                {option === ''
                  ? t('frontier.runs.all')
                  : t(`frontier.runStatus.${option}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2">
          <span className="text-gray-500 dark:text-gray-400">
            {t('frontier.runs.filterRoutine')}
          </span>
          <select
            aria-label={t('frontier.runs.filterRoutine')}
            className="rounded border border-input bg-background px-2 py-1"
            value={routineId}
            onChange={(event) => setRoutineId(event.target.value)}
          >
            <option value="">{t('frontier.runs.all')}</option>
            {(routines.data ?? []).map((routine) => (
              <option key={routine.id} value={routine.id}>
                {routine.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {canStop && (
        <div className="space-y-2" data-testid="frontier-runs-actions">
          {switchActive && (
            <p
              className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200"
              data-testid="frontier-runs-emergency-active"
            >
              {t('frontier.runs.emergencyActive')}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2">
            {!switchActive && (
              <>
                <Button
                  variant="destructive"
                  size="sm"
                  disabled={selected.size === 0 || pending}
                  onClick={() => setConfirm('selected')}
                >
                  {t('frontier.runs.stopSelected')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={stoppableIds.length === 0 || pending}
                  onClick={() => setConfirm('all')}
                >
                  {t('frontier.runs.stopAll')}
                </Button>
                {stoppableIds.length === 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pending}
                    onClick={() => setConfirm('block')}
                  >
                    {t('frontier.runs.blockNew')}
                  </Button>
                )}
              </>
            )}
            {switchActive && (
              <Button
                variant="outline"
                size="sm"
                disabled={pending}
                onClick={() => setConfirm('clear')}
              >
                {t('frontier.runs.reactivate')}
              </Button>
            )}
            {!switchActive && stoppableIds.length === 0 && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {t('frontier.runs.noStoppable')}
              </span>
            )}
            {lastResult && (
              <span
                className="text-xs text-gray-500 dark:text-gray-400"
                data-testid="frontier-runs-stop-result"
              >
                {t('frontier.runs.stopResult', {
                  stopped: lastResult.stopped,
                  skipped: lastResult.skipped,
                })}
              </span>
            )}
            {actionError && (
              <span className="text-xs text-red-600 dark:text-red-400" role="alert">
                {t('frontier.runs.actionError')}
              </span>
            )}
          </div>
        </div>
      )}

      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <div className="flex items-center gap-2 py-6 text-sm text-gray-500">
              <Spinner size="sm" />
            </div>
          ) : isError ? (
            <p className="py-6 text-sm text-red-600" role="alert">
              {t('frontier.actions.failed')}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  {canStop && (
                    <TableHead className="w-8">
                      <input
                        type="checkbox"
                        aria-label={t('frontier.runs.selectAll')}
                        checked={allSelected}
                        onChange={toggleAll}
                      />
                    </TableHead>
                  )}
                  <TableHead>{t('frontier.runs.colId')}</TableHead>
                  <TableHead>{t('frontier.runs.colRoutine')}</TableHead>
                  <TableHead>{t('frontier.runs.colOrigin')}</TableHead>
                  <TableHead>{t('frontier.runs.colStatus')}</TableHead>
                  <TableHead>{t('frontier.runs.colWaitReason')}</TableHead>
                  <TableHead>{t('frontier.runs.colSteps')}</TableHead>
                  <TableHead>{t('frontier.runs.colQueuedAt')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.length === 0 && (
                  <TableEmpty
                    colSpan={canStop ? 8 : 7}
                    title={t('frontier.runs.empty')}
                  />
                )}
                {items.map((run) => (
                  <TableRow key={run.id} data-testid={`run-row-${run.id}`}>
                    {canStop && (
                      <TableCell>
                        <input
                          type="checkbox"
                          aria-label={t('frontier.runs.selectRun')}
                          data-testid={`run-select-${run.id}`}
                          disabled={!STOPPABLE_STATUSES.has(run.status)}
                          checked={selected.has(run.id)}
                          onChange={() => toggleOne(run.id)}
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <Link
                        className="font-mono text-xs text-blue-600 hover:underline"
                        to={`/frontier/runs/${run.id}`}
                      >
                        {run.id}
                      </Link>
                    </TableCell>
                    <TableCell>{run.routine_name ?? '—'}</TableCell>
                    <TableCell>{t(`frontier.origins.${run.origin}`)}</TableCell>
                    <TableCell>
                      <StatusChip
                        kind="run"
                        value={run.status}
                        label={t(`frontier.runStatus.${run.status}`)}
                      />
                    </TableCell>
                    <TableCell>
                      {run.wait_reason ? t(`frontier.waits.${run.wait_reason}`) : '—'}
                    </TableCell>
                    <TableCell>{run.steps_used}</TableCell>
                    <TableCell>{formatDateTime(run.queued_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={confirm !== null} onOpenChange={(open) => !open && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm === 'clear'
                ? t('frontier.runs.reactivateTitle')
                : confirm === 'block'
                  ? t('frontier.runs.blockConfirmTitle')
                  : t('frontier.runs.stopConfirmTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm === 'clear'
                ? t('frontier.runs.reactivateBody')
                : confirm === 'block'
                  ? t('frontier.runs.blockConfirmBody')
                  : confirm === 'all'
                    ? t('frontier.runs.stopConfirmAll')
                    : t('frontier.runs.stopConfirmSelected', { count: selected.size })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('frontier.runs.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              disabled={pending}
              onClick={() => {
                void (confirm === 'selected'
                  ? stopSelected()
                  : confirm === 'clear'
                    ? reactivate()
                    : stopAll());
              }}
            >
              {confirm === 'clear'
                ? t('frontier.runs.reactivate')
                : confirm === 'block'
                  ? t('frontier.runs.blockConfirmAction')
                  : t('frontier.runs.stopConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
